/**
 * The window a zone's waveform shows onto its file.
 *
 * A zone editor that always drew the whole file made setting a start inside a
 * long recording a guess to within a few hundred milliseconds: at 240 pixels a
 * ten-minute file is two and a half seconds a pixel. The window is a span of
 * the file in seconds, and everything drawn or grabbed on the canvas goes
 * through the two conversions here, so what is drawn and what is clickable
 * cannot disagree — the same rule the timeline keeps.
 *
 * Pure functions over plain numbers, so the zoom and the pan can be tested
 * without a canvas, which jsdom cannot measure anyway.
 */

export interface ZoneView {
  fromSec: number;
  toSec: number;
}

/** The narrowest window worth showing: below this a pixel is under a sample. */
export const MIN_ZONE_VIEW_SEC = 0.005;

/** The whole file. */
export function wholeFile(totalSec: number): ZoneView {
  return { fromSec: 0, toSec: Math.max(MIN_ZONE_VIEW_SEC, totalSec) };
}

/** Seconds at a fraction of the way across the canvas. */
export function secondsAtFraction(view: ZoneView, fraction: number): number {
  return view.fromSec + fraction * (view.toSec - view.fromSec);
}

/** Where a moment of the file falls across the canvas, as a fraction. */
export function fractionOfSeconds(view: ZoneView, seconds: number): number {
  const span = view.toSec - view.fromSec;
  return span <= 0 ? 0 : (seconds - view.fromSec) / span;
}

/** A window pushed back inside the file, keeping its width where it can. */
export function clampView(view: ZoneView, totalSec: number): ZoneView {
  const total = Math.max(MIN_ZONE_VIEW_SEC, totalSec);
  const span = Math.min(total, Math.max(MIN_ZONE_VIEW_SEC, view.toSec - view.fromSec));
  const fromSec = Math.min(total - span, Math.max(0, view.fromSec));
  return { fromSec, toSec: fromSec + span };
}

/**
 * The window scaled by `factor` around the moment under the pointer, which
 * stays under the pointer: zooming in on a transient keeps the transient where
 * it was on screen rather than sliding it off to one side.
 */
export function zoomView(view: ZoneView, factor: number, anchorFraction: number, totalSec: number): ZoneView {
  const anchorSec = secondsAtFraction(view, anchorFraction);
  const span = (view.toSec - view.fromSec) / Math.max(1e-6, factor);
  const fromSec = anchorSec - anchorFraction * span;
  return clampView({ fromSec, toSec: fromSec + span }, totalSec);
}

/** The window slid along the file by a fraction of its own width. */
export function panView(view: ZoneView, byFraction: number, totalSec: number): ZoneView {
  const shift = byFraction * (view.toSec - view.fromSec);
  return clampView({ fromSec: view.fromSec + shift, toSec: view.toSec + shift }, totalSec);
}

/**
 * The window that shows a region with a little room either side, so both of
 * its edges are on screen and grabbable rather than flush with the border.
 */
export function fitRegion(offsetSec: number, durationSec: number, totalSec: number, margin = 0.1): ZoneView {
  const span = Math.max(MIN_ZONE_VIEW_SEC, durationSec);
  const pad = span * margin;
  // Room that would fall outside the file is simply not there, rather than
  // being made up on the other side by sliding the window along.
  const total = Math.max(MIN_ZONE_VIEW_SEC, totalSec);
  const fromSec = Math.max(0, offsetSec - pad);
  const toSec = Math.min(total, offsetSec + span + pad);
  return clampView({ fromSec, toSec }, totalSec);
}

/** Which samples a canvas of `width` pixels reads to draw the window. */
export function envelopeRequest(
  view: ZoneView,
  width: number,
  sampleRate: number,
): { startSample: number; samplesPerPixel: number } {
  const spanSec = view.toSec - view.fromSec;
  return {
    startSample: view.fromSec * sampleRate,
    samplesPerPixel: Math.max(1, (spanSec / Math.max(1, width)) * sampleRate),
  };
}

/** True when the window is the whole file, within a sample. */
export function showsWholeFile(view: ZoneView, totalSec: number): boolean {
  return view.fromSec <= 1e-6 && view.toSec >= totalSec - 1e-6;
}
